import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { AgentInsightsPreview } from "./AgentInsightsPreview";

interface AgentInsightsSectionProps {
  northId: string;
  primaryColor?: string | null;
  forceMobileLayout?: boolean;
}

export const AgentInsightsSection = ({ northId, primaryColor, forceMobileLayout }: AgentInsightsSectionProps) => {
  const { data: insights, isLoading, error } = useQuery({
    queryKey: ['agent-insights-section', northId],
    queryFn: async () => {
      // For demo report, always fetch from the actual demo north report
      const isDemo = northId === 'aaaaaaaa-0000-0000-0000-000000000001';
      const targetNorthId = isDemo ? 'd0daa852-5636-45c6-8a29-0d12ba9cf2d1' : northId;

      const { data, error } = await supabase
        .from('agent_insights')
        .select('*')
        .eq('north_id', targetNorthId)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') throw error;
      return data;
    },
  });

  // Fetch the north to check if insights should be shown
  const { data: north } = useQuery({
    queryKey: ['north-show-insights', northId],
    queryFn: async () => {
      const isDemo = northId === 'alyssum';
      const targetNorthId = isDemo ? 'd0daa852-5636-45c6-8a29-0d12ba9cf2d1' : northId;

      const { data, error } = await supabase
        .from('norths')
        .select('show_agent_insights_on_report')
        .eq('id', targetNorthId)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') throw error;
      return data;
    },
  });

  // For demo reports, always show the section even without data
  const isDemoReport = northId === 'alyssum';

  // Don't render anything while loading
  if (isLoading) {
    return null;
  }

  // Check if the section should be shown on the report
  if (!isDemoReport && north?.show_agent_insights_on_report !== true) {
    return null;
  }

  // For demo, always render with fallback. For regular reports, only render if we have data
  if (!isDemoReport && (!insights || !insights.ai_summary)) {
    return null;
  }

  return (
    <div className="mb-12 p-8 pb-4 rounded-xl bg-gradient-to-br from-amber-50/50 via-amber-100/30 to-background border border-border/50 shadow-sm">
      <AgentInsightsPreview
        northId={northId}
        primaryColor={primaryColor}
      />
    </div>
  );
};
